/**
 * Maxio "full workflow" systems landscape, left to right (Maxio partner deck, slide 8):
 *
 *   [retired systems strip]                                  (red, no arrows)
 *   [Maxio CPQ]
 *   [CRM]  <---->  [ MAXIO + add-on chips ]  <---->  [ERP / GL]
 *                     ^      ^      ^
 *                     [tax] [payments] [website] [custom]
 *
 * Front office (CRM, and Maxio CPQ on top of it, since CPQ runs in the CRM) on the left, Maxio in
 * the centre, back office (general ledger) on the right, the side systems under Maxio. Every card
 * carries the technology's logo, or an initials badge when no logo is bundled. Maxio deals use the
 * defaults below; Rillet deals reuse the layout with their own hub, columns and plan colours
 * (`rillet-landscape.ts`). Every other product keeps the hub-and-spoke `buildTechStackLandscape`.
 */
import { landscapeInitials, landscapeLogo } from './logos.js';
import { TECH_STACK_HUB_ID, } from './tech-stack.js';
const PAD_X = 32;
const COL_W = 210;
const HUB_W = 290;
const GAP_X = 110;
const NODE_H = 112;
const SMALL_W = 170;
const SMALL_H = 104;
const HUB_BASE_H = 122;
const STACK_GAP = 40;
const ROW_GAP = 64;
const STRIP_TOP = 22;
const STRIP_HEADER = 22;
const BOTTOM_PAD = 32;
const LEFT_X = PAD_X;
const HUB_X = LEFT_X + COL_W + GAP_X;
const RIGHT_X = HUB_X + HUB_W + GAP_X;
const BASE_WIDTH = RIGHT_X + COL_W + PAD_X;
const MAXIO_FLOW_PLACEMENT = {
    left: ['crm', 'cpq', 'billing-engines'],
    right: ['erp'],
    bottom: ['maxio-product', 'taxes', 'payments', 'custom-integration'],
};
// Corridors for system-to-system lines: outside the side stacks and under the bottom row.
const CORRIDOR_GAP = 12;
const CORRIDOR_STEP = 6;
const HUB_ACCENT = '#4f46e5';
function edgeEnds(direction, systemId) {
    if (direction === 'outbound')
        return { source: TECH_STACK_HUB_ID, target: systemId, bidirectional: false };
    if (direction === 'inbound')
        return { source: systemId, target: TECH_STACK_HUB_ID, bidirectional: false };
    return { source: systemId, target: TECH_STACK_HUB_ID, bidirectional: true };
}
/** Evenly spaced points along a segment (for fanning several edges into one side of the hub). */
function fan(start, length, count) {
    return Array.from({ length: count }, (_, i) => start + (length * (i + 1)) / (count + 1));
}
export function buildMaxioFlowLandscape(systems, options = {}) {
    const named = systems.filter((s) => s.name.trim().length > 0);
    const empty = {
        canvasWidth: BASE_WIDTH,
        canvasHeight: 2 * STRIP_TOP + HUB_BASE_H,
        headerTop: STRIP_TOP,
        headerHeight: STRIP_HEADER,
        columns: [],
        nodes: [],
        edges: [],
    };
    if (named.length === 0)
        return empty;
    const logoFor = options.logoFor ?? landscapeLogo;
    const labelOf = (s) => options.categoryLabels?.[s.category] ?? s.category;
    const card = (s, x, y, w, h) => {
        const retired = s.plan === 'sunset';
        const logo = logoFor(s.logoKey);
        const tbd = options.planTone && s.plan === 'tbd';
        return {
            id: s.id,
            x,
            y,
            width: w,
            height: h,
            kind: 'regular',
            variant: 'default',
            label: labelOf(s),
            currentTool: s.name,
            postPlan: retired ? 'Being retired' : tbd ? 'TBD' : null,
            tone: retired
                ? 'danger'
                : options.planTone && s.plan === 'keep'
                    ? 'success'
                    : 'neutral',
            embeddedChild: null,
            logo,
            initials: logo ? null : landscapeInitials(s.name),
        };
    };
    const retired = named.filter((s) => s.plan === 'sunset');
    const live = named.filter((s) => s.plan !== 'sunset');
    const cpqInCrm = live.find((s) => s.category === 'cpq' &&
        s.integration?.flowTarget &&
        s.integration.flowTarget !== TECH_STACK_HUB_ID);
    const placement = options.placement ?? MAXIO_FLOW_PLACEMENT;
    const inCats = (cats) => cats.flatMap((c) => live.filter((s) => s.category === c && s !== cpqInCrm));
    const left = [...(cpqInCrm ? [cpqInCrm] : []), ...inCats(placement.left)];
    const right = inCats(placement.right);
    const bottom = inCats(placement.bottom);
    const placed = new Set([...left, ...right, ...bottom]);
    // Anything uncategorised still gets a box under Maxio.
    for (const s of live)
        if (!placed.has(s))
            bottom.push(s);
    const nodes = [];
    const edges = [];
    const columns = [];
    // Retired strip (top-left).
    let y = STRIP_TOP;
    if (retired.length > 0) {
        columns.push({ id: 'left', label: 'Being retired', x: LEFT_X, width: SMALL_W });
        y += STRIP_HEADER + 8;
        retired.forEach((s, i) => nodes.push(card(s, LEFT_X + i * (SMALL_W + 16), y, SMALL_W, SMALL_H)));
        y += SMALL_H + ROW_GAP;
    }
    else {
        y += 8;
    }
    // Main row: left stack | hub | right stack, centred on one line.
    const chips = options.addOns ?? [];
    const hubH = HUB_BASE_H + (chips.length > 0 ? 26 * Math.ceil(chips.length / 2) + 4 : 0);
    const stackH = (n) => (n === 0 ? 0 : n * NODE_H + (n - 1) * STACK_GAP);
    const rowH = Math.max(hubH, stackH(left.length), stackH(right.length));
    const centre = y + rowH / 2;
    const hubY = centre - hubH / 2;
    nodes.push({
        id: TECH_STACK_HUB_ID,
        x: HUB_X,
        y: hubY,
        width: HUB_W,
        height: hubH,
        kind: 'regular',
        variant: 'default',
        label: 'Platform',
        currentTool: options.hubName?.trim() || 'Maxio',
        postPlan: options.product ?? null,
        tone: 'neutral',
        embeddedChild: null,
        logo: logoFor(options.hubLogoKey ?? 'maxio'),
        initials: logoFor(options.hubLogoKey ?? 'maxio') ? null : (options.hubInitials ?? 'MX'),
        chips,
        accent: HUB_ACCENT,
    });
    const placeStack = (list, x) => {
        let sy = centre - stackH(list.length) / 2;
        for (const s of list) {
            nodes.push(card(s, x, sy, COL_W, NODE_H));
            sy += NODE_H + STACK_GAP;
        }
    };
    placeStack(left, LEFT_X);
    placeStack(right, RIGHT_X);
    // Bottom row under the hub.
    // Leave room for one routing level per box on the busier side.
    const bottomY = y + rowH + Math.max(ROW_GAP, 40 + Math.ceil(bottom.length / 2) * 12);
    const rowW = bottom.length * SMALL_W + Math.max(0, bottom.length - 1) * 20;
    const hubCx = HUB_X + HUB_W / 2;
    const startX = Math.max(PAD_X, hubCx - rowW / 2);
    bottom.forEach((s, i) => nodes.push(card(s, startX + i * (SMALL_W + 20), bottomY, SMALL_W, SMALL_H)));
    const canvasWidth = Math.max(BASE_WIDTH, startX + rowW + PAD_X);
    const canvasHeight = (bottom.length > 0 ? bottomY + SMALL_H : y + rowH) + BOTTOM_PAD;
    // Edges.
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const hub = nodeById.get(TECH_STACK_HUB_ID);
    // A system whose integration names another live card links straight to it, not to the hub
    // (Maxio CPQ-in-CRM has its own link below). Retired cards never get a line.
    const sideOf = new Map();
    for (const s of left)
        sideOf.set(s.id, 'left');
    for (const s of right)
        sideOf.set(s.id, 'right');
    for (const s of bottom)
        sideOf.set(s.id, 'bottom');
    const directTarget = (s) => {
        const ft = s.integration?.flowTarget;
        return s !== cpqInCrm && ft && ft !== s.id && sideOf.has(ft) ? ft : null;
    };
    const toHub = (s) => !!s.integration && directTarget(s) === null;
    const leftLinked = left.filter((s) => toHub(s) && s !== cpqInCrm);
    const leftYs = fan(hub.y, hub.height, leftLinked.length);
    leftLinked.forEach((s, i) => {
        const n = nodeById.get(s.id);
        const ends = edgeEnds(s.integration.direction, s.id);
        const sysPt = { x: n.x + n.width, y: n.y + n.height / 2 };
        const hubPt = { x: hub.x, y: leftYs[i] };
        const fromSys = ends.source === s.id;
        edges.push({
            id: `edge-${s.id}`,
            source: ends.source,
            target: ends.target,
            sourceHandle: fromSys ? 'right' : 'left',
            targetHandle: fromSys ? 'left' : 'right',
            bidirectional: ends.bidirectional,
            sourcePoint: fromSys ? sysPt : hubPt,
            targetPoint: fromSys ? hubPt : sysPt,
        });
    });
    if (cpqInCrm) {
        // Maxio CPQ runs inside the CRM: a vertical two-way link CPQ <-> CRM, not to the platform.
        const cpq = nodeById.get(cpqInCrm.id);
        const target = nodeById.get(cpqInCrm.integration.flowTarget);
        if (target) {
            edges.push({
                id: `edge-${cpqInCrm.id}`,
                source: cpq.id,
                target: target.id,
                sourceHandle: 'bottom',
                targetHandle: 'top',
                bidirectional: true,
            });
        }
    }
    const rightLinked = right.filter(toHub);
    const rightYs = fan(hub.y, hub.height, rightLinked.length);
    rightLinked.forEach((s, i) => {
        const n = nodeById.get(s.id);
        const ends = edgeEnds(s.integration.direction, s.id);
        const sysPt = { x: n.x, y: n.y + n.height / 2 };
        const hubPt = { x: hub.x + hub.width, y: rightYs[i] };
        const fromSys = ends.source === s.id;
        edges.push({
            id: `edge-${s.id}`,
            source: ends.source,
            target: ends.target,
            sourceHandle: fromSys ? 'left' : 'right',
            targetHandle: fromSys ? 'right' : 'left',
            bidirectional: ends.bidirectional,
            sourcePoint: fromSys ? sysPt : hubPt,
            targetPoint: fromSys ? hubPt : sysPt,
        });
    });
    // Bottom row: a box under the hub gets a straight line; boxes further out are routed on nested
    // levels (outermost highest), so no two routes overlap or cross.
    const bottomLinked = bottom.filter(toHub);
    const hubBottom = hub.y + hub.height;
    const inset = 24;
    const straight = (cx) => cx >= hub.x + inset && cx <= hub.x + hub.width - inset;
    const centreOf = (s) => {
        const n = nodeById.get(s.id);
        return n.x + n.width / 2;
    };
    const outerLeft = bottomLinked.filter((s) => centreOf(s) < hub.x + inset);
    const outerRight = bottomLinked.filter((s) => centreOf(s) > hub.x + hub.width - inset).reverse();
    const levelGap = 12;
    // Start below the whole main row, so no route passes behind a CRM / ERP card.
    const firstLevel = Math.max(hubBottom, y + rowH) + 16;
    const hubPointFor = new Map();
    for (const s of bottomLinked)
        if (straight(centreOf(s)))
            hubPointFor.set(s.id, { x: centreOf(s) });
    outerLeft.forEach((s, i) => {
        hubPointFor.set(s.id, { x: hub.x + 14 + i * 10, via: firstLevel + i * levelGap });
    });
    outerRight.forEach((s, i) => {
        hubPointFor.set(s.id, { x: hub.x + hub.width - 14 - i * 10, via: firstLevel + i * levelGap });
    });
    for (const s of bottomLinked) {
        const n = nodeById.get(s.id);
        const ends = edgeEnds(s.integration.direction, s.id);
        const hp = hubPointFor.get(s.id);
        const sysPt = { x: n.x + n.width / 2, y: n.y };
        const hubPt = { x: hp.x, y: hubBottom };
        const fromSys = ends.source === s.id;
        edges.push({
            id: `edge-${s.id}`,
            source: ends.source,
            target: ends.target,
            sourceHandle: fromSys ? 'top' : 'bottom',
            targetHandle: fromSys ? 'bottom' : 'top',
            bidirectional: ends.bidirectional,
            sourcePoint: fromSys ? sysPt : hubPt,
            targetPoint: fromSys ? hubPt : sysPt,
            ...(hp.via !== undefined ? { via: hp.via } : {}),
        });
    }
    // System-to-system lines run in corridors, so they never cross a card: outside the left or
    // right stack, and under the bottom row (or the main row when there is no bottom row). Lines
    // between two sides go down their side's corridor and along the bottom one.
    const direct = [...left, ...right, ...bottom].filter((s) => directTarget(s) !== null);
    const corridorBottom = canvasHeight - BOTTOM_PAD + CORRIDOR_GAP;
    let corridorMax = 0;
    direct.forEach((s, k) => {
        const ends = edgeEnds(s.integration.direction, s.id);
        const otherId = directTarget(s);
        const sourceId = ends.source === s.id ? s.id : otherId;
        const targetId = ends.source === s.id ? otherId : s.id;
        // Side lanes stay inside the canvas padding; the bottom lane grows the canvas instead.
        const sideStep = Math.min(k, 3) * CORRIDOR_STEP;
        const lane = {
            left: LEFT_X - CORRIDOR_GAP - sideStep,
            right: RIGHT_X + COL_W + CORRIDOR_GAP + sideStep,
            bottom: corridorBottom + k * CORRIDOR_STEP,
        };
        corridorMax = Math.max(corridorMax, lane.bottom);
        // From a card out to its corridor: [point on the card, point in the corridor].
        const exit = (id) => {
            const n = nodeById.get(id);
            const side = sideOf.get(id);
            const cy = n.y + n.height / 2;
            if (side === 'left') {
                return { side, handle: 'left', at: { x: n.x, y: cy }, out: { x: lane.left, y: cy } };
            }
            if (side === 'right') {
                return {
                    side,
                    handle: 'right',
                    at: { x: n.x + n.width, y: cy },
                    out: { x: lane.right, y: cy },
                };
            }
            const cx = n.x + n.width / 2;
            return {
                side,
                handle: 'bottom',
                at: { x: cx, y: n.y + n.height },
                out: { x: cx, y: lane.bottom },
            };
        };
        const a = exit(sourceId);
        const b = exit(targetId);
        const waypoints = [a.out];
        if (a.side !== b.side) {
            if (a.side !== 'bottom')
                waypoints.push({ x: a.out.x, y: lane.bottom });
            if (b.side !== 'bottom')
                waypoints.push({ x: b.out.x, y: lane.bottom });
        }
        waypoints.push(b.out);
        edges.push({
            id: `edge-${s.id}`,
            source: sourceId,
            target: targetId,
            sourceHandle: a.handle,
            targetHandle: b.handle,
            bidirectional: ends.bidirectional,
            sourcePoint: a.at,
            targetPoint: b.at,
            waypoints,
        });
    });
    const finalHeight = direct.length > 0 ? Math.max(canvasHeight, corridorMax + BOTTOM_PAD / 2) : canvasHeight;
    return {
        canvasWidth,
        canvasHeight: finalHeight,
        headerTop: STRIP_TOP,
        headerHeight: STRIP_HEADER,
        columns,
        nodes,
        edges,
    };
}
//# sourceMappingURL=maxio-flow-layout.js.map